import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCheck, faLock, faTimes } from '@fortawesome/free-solid-svg-icons'

import { Portal } from '@/components'
import { ModalFormProps } from '@/components/Modal/types'
import { useAppContext } from '@/hooks'
import { splitProps } from '@/utils/collections'

import Button from '../Button/Button'
import { Form, Submit } from '../UberForm'
import { Modal } from './Modal'

export const ModalForm = <T extends object>(props: ModalFormProps<T>) => {
	const { t } = useAppContext()

	const {
		cancelIcon,
		cancelSchema,
		cancelTitle,
		children,
		disabled,
		footer,
		footerBefore,
		hasLockButton,
		hasPushButton,
		hasSubmitButton = true,
		isLocked,
		onLock,
		onPush,
		saveIcon,
		saveSchema,
		saveTitle,
		testId,
	} = props

	let iconSave: IconProp = faCheck

	if (saveIcon) {
		iconSave = saveIcon
	}

	let iconCancel: IconProp = faTimes

	if (cancelIcon) {
		iconCancel = cancelIcon
	}

	const formProps = { ...props }

	const modalProps = splitProps(formProps, [
		'children',
		'contentStyle',
		'bodyStyle',
		'header',
		'footer',
		'open',
		'onClose',
		'disablePortal',
		'headerStyle',
		'resizable',
		'maximizeButtonVisible',
		'closeButtonVisible',
		'maximized',
		'height',
		'isLocked',
		'dialogId',
	])

	return (
		<Portal>
			<Form<T> ref={props.formRef} {...formProps} disabled={disabled}>
				<Modal
					{...modalProps}
					disablePortal
					footer={(close) =>
						footer || (
							<>
								{footerBefore}
								<div className="flex w-full justify-end">
									<div className="flex">
										{/* MDD-2596: Cancel first, then Save (same order as FullscreenEditorModal) */}
										<Button
											onClick={close}
											schema={cancelSchema || 'transparent'}
											icon={iconCancel}
											className="mr-2"
											testId={`modal-cancel-${testId}`}
										>
											{cancelTitle || t('CANCEL')}
										</Button>
										{hasLockButton && (
											<Button
												className="mr-2"
												schema="warn"
												icon={faLock}
												onClick={onLock}
												disabled={isLocked}
												testId={`modal-lock-${testId}`}
											>
												{t('LOCK')}
											</Button>
										)}
										{hasPushButton && (
											<Button
												className="mr-2"
												schema={saveSchema || 'success'}
												icon={iconSave}
												onClick={onPush}
												disabled={disabled}
												testId={`modal-push-${testId}`}
											>
												{saveTitle || t('PUSH')}
											</Button>
										)}
										{hasSubmitButton && (
											<Submit
												schema={saveSchema || 'primary'}
												icon={iconSave}
												disabled={disabled}
												testId={`modal-save-${testId}`}
											>
												{saveTitle || t('SAVE')}
											</Submit>
										)}
									</div>
								</div>
							</>
						)
					}
				>
					{children}
				</Modal>
			</Form>
		</Portal>
	)
}
